import { computed, onBeforeMount, ref } from 'vue'
import { useSnackbar, SnackbarType } from '@tapp.online/ui'
import type { UserRole, Company, ChildCompany } from '@tapp.online/auth/runtime/types/index.js'

export type WorkspaceCompanyRow = { kind: 'company'; key: string; userRole: UserRole; company: Company | undefined; isActive: boolean }
export type WorkspaceChildRow = { kind: 'child'; key: string; childCompany: ChildCompany; parentUserRoleId: string; isActive: boolean }
export type WorkspaceRow = WorkspaceCompanyRow | WorkspaceChildRow

// Shared with the dashboard's switcher, so both order workspaces by the same recents
const RECENTS_STORAGE_KEY = 'tapp:company-switcher:recents'
const MAX_RECENTS = 4

/** The workspace list and switch logic of the dashboard's company switcher (tapp-dashboard-v2 components/companySwitcher/Modal.vue), without the search. */
export const useWorkspaceSwitch = () => {
    const { userRoles, activeUserRole, activeCompany, switchCompany, switchToChildCompany } = useAuth()
    const { addSnackbar } = useSnackbar()

    const switching = ref(false)

    const loadRecents = (): string[] => {
        try {
            const stored = localStorage.getItem(RECENTS_STORAGE_KEY)
            return stored ? (JSON.parse(stored) as string[]) : []
        } catch {
            return []
        }
    }

    // Before mount rather than at setup: storage only exists in the browser, and this still lands before the first render
    const recentUserRoleIds = ref<string[]>([])
    onBeforeMount(() => {
        recentUserRoleIds.value = loadRecents()
    })

    const persistRecents = () => {
        try {
            localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(recentUserRoleIds.value))
        } catch {
            // Ignore storage failures (private mode / quota); recents are a nicety, not critical state.
        }
    }

    const workspaceInitials = (name?: string | null) => {
        if (!name) return ''
        return `${name[0]?.toUpperCase() ?? ''}${name[1]?.toUpperCase() ?? ''}`
    }

    const rowName = (row: WorkspaceRow) => (row.kind === 'company' ? row.company?.name : row.childCompany.name)
    const rowShortCode = (row: WorkspaceRow) => (row.kind === 'company' ? row.company?.shortCode : row.childCompany.shortCode)

    const isActiveWorkspace = (userRoleId: string, companyId?: string) => userRoleId === activeUserRole.value?.userRoleId && companyId === activeUserRole.value?.companyId

    const companyRows = computed<WorkspaceCompanyRow[]>(() =>
        userRoles.value
            .filter((userRole) => userRole.company)
            .map((userRole) => ({
                kind: 'company',
                key: `company:${userRole.id}`,
                userRole,
                company: userRole.company,
                isActive: isActiveWorkspace(userRole.id, userRole.companyId)
            }))
    )

    const buildChildRows = (parentUserRole: UserRole): WorkspaceChildRow[] =>
        (parentUserRole.childCompanies ?? []).map((childCompany) => ({
            kind: 'child',
            key: `child:${childCompany.id}`,
            childCompany,
            parentUserRoleId: parentUserRole.id,
            isActive: isActiveWorkspace(parentUserRole.id, childCompany.id)
        }))

    // Recent workspaces first, each followed by its child companies
    const workspaceRows = computed<WorkspaceRow[]>(() => {
        const recentPosition = (userRoleId: string) => {
            const position = recentUserRoleIds.value.indexOf(userRoleId)
            return position === -1 ? Number.MAX_SAFE_INTEGER : position
        }
        const ordered = [...companyRows.value].sort((first, second) => recentPosition(first.userRole.id) - recentPosition(second.userRole.id))
        return ordered.flatMap((companyRow) => [companyRow, ...buildChildRows(companyRow.userRole)])
    })

    /** Resolves to true once the workspace is active, including when it already was. */
    const switchWorkspace = async (row: WorkspaceRow) => {
        if (switching.value) return false
        // Selecting the workspace you're already in is a no-op
        if (row.isActive) return true

        switching.value = true
        try {
            if (row.kind === 'company') {
                await switchCompany(row.userRole.id)
                recentUserRoleIds.value = [row.userRole.id, ...recentUserRoleIds.value.filter((id) => id !== row.userRole.id)].slice(0, MAX_RECENTS)
                persistRecents()
            } else {
                await switchToChildCompany(row.parentUserRoleId, row.childCompany.id)
            }

            addSnackbar({
                title: 'Workspace switched',
                text: `Now viewing ${rowName(row)}`,
                type: SnackbarType.SUCCESS,
                duration: 2400
            })
            return true
        } catch (error) {
            addSnackbar({
                title: 'Failed to switch workspace',
                text: 'Something went wrong',
                type: SnackbarType.ERROR,
                duration: 5000
            })
            console.error(error)
            return false
        } finally {
            switching.value = false
        }
    }

    return { activeCompany, workspaceRows, switching, switchWorkspace, workspaceInitials, rowName, rowShortCode }
}
